import { useState, useEffect } from "react";
import Dropdown from "../components/Dropdown";
import TextInput from "../components/TextInput";
import { useData } from "../contexts/DataContext";
import { eventTypes } from "../constants/eventTypes";
import { validateForm, ValidationError } from "../utils/validation";
import { eventValidationSchema } from "../validations/eventValidationSchema";
import { getFirestore, collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { useFirebase } from "../contexts/FirebaseContext";
import ReactConfetti from "react-confetti";
import { COUNTRIES, getRegionsByCountry } from "../constants/locations";
import InfoTooltip from "../components/InfoTooltip";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/Buttons/Button";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ImageUpload from "../components/ImageUpload";
import { formatUrl } from "../utils/tools";
import { encode } from "blurhash";
import EventTypeSelect from "../components/EventTypeSelect";
import { useSearchParams } from "react-router-dom";

const getImageData = (image: HTMLImageElement): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext("2d");
    if (!context) reject(new Error("Could not get canvas context"));
    
    context!.drawImage(image, 0, 0);
    resolve(context!.getImageData(0, 0, image.width, image.height));
  });
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const SubmitEvent = () => {
  const [searchParams] = useSearchParams();
  const duplicateEventId = searchParams.get("duplicate");
  const editEventId = searchParams.get("edit");
  const preLoad = duplicateEventId || editEventId;
  const [id, setId] = useState<string | null>(preLoad ? (editEventId || duplicateEventId) : null);
  const { providerData, eventsData, loading } = useData();
  const { currentUser } = useAuth();
  const {app, storage} = useFirebase();

  // Find the matching provider
  const userProvider = providerData.find(
    (provider) => provider.owner_id === currentUser?.uid
  );

  // Get the page title based on URL params
  const getPageTitle = () => {
    if (editEventId) {
      return "Edit Event";
    } else if (duplicateEventId) {
      return "Duplicate Event";
    }
    return "Create New Event";
  };

  const [formData, setFormData] = useState(() => {
    const defaultState = {
      event_link: "",
      event_name: "",
      event_description: "",
      date: "",
      start_time: "",
      end_time: "",
      venue: "",
      address: "",
      event_country: userProvider?.provider_country || "",
      event_region: userProvider?.provider_region || "",
      event_postcode: userProvider?.provider_postcode || "",
      event_types: [] as string[],
      provider_name: userProvider?.provider_name || "",
      header_image: "",
      header_image_blur_hash: "",
    };

    if (preLoad) {
      const event = eventsData.find(event => event.id === duplicateEventId || event.id === editEventId);
      if (event) {
       let preLoadedState = {
          ...defaultState,
          event_name: event.event_name,
          event_description: event.event_description,
          event_link: event.event_link || "",
          start_time: event.start_time || "",
          end_time: event.end_time || "",
          venue: event.venue || "",
          address: event.address || "",
          event_country: event.event_country || defaultState.event_country,
          event_region: event.event_region || defaultState.event_region,
          event_postcode: event.event_postcode || defaultState.event_postcode,
          event_types: event.event_types || [],
          provider_name: event.provider_name || defaultState.provider_name,
          header_image: event.header_image || "",
          header_image_blur_hash: event.header_image_blur_hash || "",
        };
        if (editEventId) {
           preLoadedState = {
            ...preLoadedState,
            date: typeof event.date === 'string' ? event.date : event.date.format('DD/MM/YYYY'),
           }
        }

        return preLoadedState;
      }
    }

    return defaultState;
  });

  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [headerImage, setHeaderImage] = useState<File | null>(null);
  const [headerImageUrl, setHeaderImageUrl] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadStatus, setImageUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (userProvider?.provider_country) {
      setAvailableRegions(getRegionsByCountry(userProvider.provider_country));
    }
  }, [userProvider]);

  useEffect(() => {
    if (formData.event_country) {
      setAvailableRegions(getRegionsByCountry(formData.event_country));
    }
  }, [formData.event_country]);

  useEffect(() => {
    if (formData.header_image) {
      setPreviewImageUrl(formData.header_image);
      setImageUploadStatus('success');
    }
  }, [formData.header_image]);

  const eventTypeOptions = eventTypes.map((eventType) => ({
    label: eventType.label,
    onClick: () => handleEventTypeSelect(eventType.type),
  }));

  const handleEventTypeSelect = (type: string) => {
    setFormData((prev) => ({ ...prev, event_type: type }));
  };

  const validateField = (
    fieldName: keyof typeof eventValidationSchema,
    value: any
  ) => {
    if (touchedFields.has(fieldName) || isSubmitted) {
      const validationResult = validateForm(
        { [fieldName]: value },
        { [fieldName]: eventValidationSchema[fieldName] }
      );

      setErrors((prev) => {
        const otherErrors = prev.filter((error) => error.field !== fieldName);
        return [...otherErrors, ...validationResult];
      });
    }
  };

  const handleChange = (
    fieldName: keyof typeof eventValidationSchema,
    value: any
  ) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    setTouchedFields((prev) => new Set(prev).add(fieldName));
    validateField(fieldName, value);
  };

  const handleOptionalChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleCountryChange = (country: string) => {
    handleOptionalChange("event_country", country);
    handleOptionalChange("event_region", "");
    const regions = getRegionsByCountry(country);
    setAvailableRegions(regions);
  };

  const handleImageUpload = async (file: File) => {
    setImageUploadStatus('uploading');
    setHeaderImage(file);
    const imageRef = ref(storage, `events/header_images/${Date.now()}_${file.name}`);
    
    try {
      const snapshot = await uploadBytes(imageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      // Generate blurhash
      const objectUrl = URL.createObjectURL(file);
      const img = await loadImage(objectUrl);
      const imageData = await getImageData(img);
      
      const blurhash = encode(
        imageData.data,
        imageData.width,
        imageData.height,
        4,  // x components
        3   // y components
      );

      URL.revokeObjectURL(objectUrl);
      
      setPreviewImageUrl(url);
      setImageUploadStatus('success');
      setFormData(prev => ({ 
        ...prev, 
        header_image: url,
        header_image_blur_hash: blurhash 
      }));
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageUploadStatus('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);

    const formDataWithFormattedUrl = {
      ...formData,
      event_link: formatUrl(formData.event_link)
    };

    const validationErrors = validateForm(formDataWithFormattedUrl, eventValidationSchema);
    setErrors(validationErrors);

    if (validationErrors.length === 0) {
      try {
        const db = getFirestore(app);
        const eventData = {
          ...formDataWithFormattedUrl,
          status: "pending",
        };

        let newId;
        if (editEventId) {
          const docRef = doc(db, "events", editEventId);
          await updateDoc(docRef, {
            ...eventData,
            updated_at: new Date(),
          });
          newId = editEventId;
        } else {
          const docRef = await addDoc(collection(db, "events"), {
            ...eventData,
            created_at: new Date(),
          });
          newId = docRef.id;
          await updateDoc(docRef, { id: newId });
        }
        
        setId(newId);
        setShowConfetti(true);
        setShowSuccessModal(true);

        // Reset form
        setFormData({
          event_link: "",
          event_name: "",
          event_description: "",
          date: "",
          start_time: "",
          end_time: "",
          venue: "",
          address: "",
          event_country: "",
          event_region: "",
          event_postcode: "",
          event_types: [],
          provider_name: "",
          header_image: "",
          header_image_blur_hash: "",
        });
        setTouchedFields(new Set());
        setIsSubmitted(false);
        setImageUploadStatus('idle');
        setPreviewImageUrl(null);

        setTimeout(() => {
          setShowConfetti(false);
        }, 5000);
      } catch (error) {
        console.error("Error saving event:", error);
      }
    }
  };

  const getFieldError = (fieldName: string) => {
    return errors.find((error) => error.field === fieldName)?.message;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">{getPageTitle()}</h1>
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <TextInput
          label="Event Name"
          value={formData.event_name}
          onChange={(e) => handleChange("event_name", e.target.value)}
          onBlur={() => validateField("event_name", formData.event_name)}
          error={getFieldError("event_name")}
          touched={touchedFields.has("event_name")}
          required
        />
        {currentUser?.admin === undefined && (
          <TextInput
            onChange={() => {}}
            label="Provider"
            value={userProvider?.provider_name || "No provider available"}
            disabled={true}
            error={
              !userProvider &&
              "You don't have any providers associated with your account"
            }
          />
        )}

        {currentUser?.admin && (
          <div>
            <label className="block text-sm font-medium mb-2">Provider</label>
            <Dropdown
              trigger={
                <div className="p-2 border rounded-lg w-full text-left bg-white">
                  {formData.provider_name || "Select provider"}
                </div>
              }
              items={providerData.map((provider) => ({
                label: provider.provider_name,
                onClick: () =>
                  handleOptionalChange("provider_name", provider.provider_name),
              }))}
            />
          </div>
        )}

        <TextInput
          label="Event Link"
          value={formData.event_link}
          onChange={(e) => handleChange("event_link", e.target.value)}
          onBlur={() => validateField("event_link", formData.event_link)}
          error={getFieldError("event_link")}
          touched={touchedFields.has("event_link")}
          type="text"
          inputMode="url"
          aria-label="Event website URL"
          required
        />

        <TextInput
          label="Event Description (max 200 characters)"
          value={formData.event_description}
          onChange={(e) => handleChange("event_description", e.target.value)}
          onBlur={() =>
            validateField("event_description", formData.event_description)
          }
          error={getFieldError("event_description")}
          touched={touchedFields.has("event_description")}
          maxLength={200}
          isTextArea
          required
        />
        {/* Event Type Dropdown */}
        <div>
          <EventTypeSelect
            selectedTypes={formData.event_types}
            onChange={(types) => handleChange("event_types", types)}
            error={getFieldError("event_types")}
            touched={touchedFields.has("event_types")}
          />
        </div>

        {/* Date Picker */}
        <TextInput
          label="Date"
          value={formData.date}
          onChange={(e) => handleChange("date", e.target.value)}
          onBlur={() => validateField("date", formData.date)}
          error={getFieldError("date")}
          touched={touchedFields.has("date")}
          placeholder="DD/MM/YYYY"
          required
        />

        {/* Time Fields */}
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="Start Time"
            value={formData.start_time}
            onChange={(e) => handleChange("start_time", e.target.value)}
            onBlur={() => validateField("start_time", formData.start_time)}
            error={getFieldError("start_time")}
            touched={touchedFields.has("start_time")}
            type="time"
            required
          />
          <TextInput
            label="End Time"
            value={formData.end_time}
            onChange={(e) => handleChange("end_time", e.target.value)}
            onBlur={() => validateField("end_time", formData.end_time)}
            error={getFieldError("end_time")}
            touched={touchedFields.has("end_time")}
            type="time"
            required
          />
        </div>

        {/* Venue and Address */}
        <TextInput
          label="Venue"
          value={formData.venue}
          onChange={(e) => handleChange("venue", e.target.value)}
          onBlur={() => validateField("venue", formData.venue)}
          error={getFieldError("venue")}
          touched={touchedFields.has("venue")}
          required
        />

        <TextInput
          label="Address"
          value={formData.address}
          onChange={(e) => handleChange("address", e.target.value)}
          onBlur={() => validateField("address", formData.address)}
          error={getFieldError("address")}
          touched={touchedFields.has("address")}
          required
        />

        {/* Optional Location Fields */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              Country
              <InfoTooltip text="If the event country is different to your profile, please specify here. Otherwise it can be left empty" />
            </label>
            <Dropdown
              trigger={
                <div className="p-2 border rounded-lg w-full text-left bg-white">
                  {formData.event_country || "Select Country"}
                </div>
              }
              items={COUNTRIES.map((country) => ({
                label: country,
                onClick: () => handleCountryChange(country),
              }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              Region
              <InfoTooltip text="If the event region is different to your profile, please specify here. Otherwise it can be left empty" />
            </label>
            <Dropdown
              trigger={
                <div className="p-2 border rounded-lg w-full text-left bg-white">
                  {formData.event_region || "Select Region"}
                </div>
              }
              items={availableRegions.map((region) => ({
                label: region,
                onClick: () => handleOptionalChange("event_region", region),
              }))}
              disabled={!formData.event_country}
            />
          </div>
          <TextInput
            label="Postcode"
            value={formData.event_postcode}
            onChange={(e) =>
              handleOptionalChange("event_postcode", e.target.value)
            }
            onBlur={() =>
              validateField("event_postcode", formData.event_postcode)
            }
            error={getFieldError("event_postcode")}
            touched={touchedFields.has("event_postcode")}
          />
        </div>

        {/* Update the ImageUpload section */}
        <div className="space-y-2">
          <label className="block text-sm font-medium">Header Image</label>
          <div className="relative">
            <div className="w-32 h-32 bg-gray-200 rounded-lg m-4 relative" />
            <ImageUpload
              onFileSelect={handleImageUpload}
              currentImage={previewImageUrl || formData.header_image}
              maxSize={500}
              imgClasses="absolute -top-2 left-0 w-32 h-32 rounded-lg m-4"
            />
          </div>
          {imageUploadStatus === 'uploading' && (
            <p className="text-blue-500 text-sm">Uploading image...</p>
          )}
          {imageUploadStatus === 'error' && (
            <p className="text-red-500 text-sm">Failed to upload image. Please try again.</p>
          )}
        </div>

        {/* Update submit button to remove onClick handler */}
        <div className="flex justify-end w-full">
          <Button 
            type="submit"
            className="w-fit"
          >
            {uploadingImage ? "Uploading..." : "Submit Event"}
          </Button>
        </div>
      </form>

      {/* Confetti */}
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-center mb-6">
              Congrats! You added an event
            </h2>
            <div className="flex flex-col space-y-4">
              <a 
                href={`/events/view?id=${id}`}
                className="w-full"
              >
                <Button className="w-full">
                  View Event
                </Button>
              </a>
              <Button
                color="moss text-black"
                onClick={() => {
                  setShowSuccessModal(false);
                  window.scrollTo(0, 0);
                }}
                className="w-full"
              >
                Add Another Event
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitEvent;
